// src/pages/onboarding/OnboardingPage.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import OnboardingLayout from '@/components/layouts/OnboardingLayout';
import Step1Welcome from '@/components/onboarding/steps/Step1Welcome';
import Step2BusinessBasics from '@/components/onboarding/steps/Step2BusinessBasics';
import Step3AIIdentity from '@/components/onboarding/steps/Step3AIIdentity';
import Step4Operations from '@/components/onboarding/steps/Step4Operations';
import Step5ConnectActivate from '@/components/onboarding/steps/Step5ConnectActivate';
import Step6Complete from '@/components/onboarding/steps/Step6Complete';

// Assuming BookableService is properly defined in your types
import { OnboardingData, initialOnboardingData, OnboardingStepProps, BookableService } from '@/types/onboarding';

interface UserSettings {
  user_id: string;
  onboarding_completed?: boolean;
  vapi_phone_number?: string | null;
  phone_provisioning_status?: string | null;
  twilio_phone_number?: string | null;
  // Add other fields from user_settings that might be relevant if fetched
}

const TOTAL_ONBOARDING_STEPS = 6;
const SESSION_STORAGE_KEY = 'onboardingProgress';

const getInitialStateFromStorage = (): { initialStep: number; initialData: OnboardingData } => {
  const savedProgress = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (savedProgress) {
    try {
      const parsed = JSON.parse(savedProgress);
      if (parsed && typeof parsed.currentStep === 'number' && typeof parsed.onboardingData === 'object' && parsed.onboardingData !== null) {
        const validStep = Math.max(1, Math.min(parsed.currentStep, TOTAL_ONBOARDING_STEPS));
        
        const restoredOnboardingData: OnboardingData = { 
          ...initialOnboardingData,
          ...parsed.onboardingData,
          bookableServices: Array.isArray(parsed.onboardingData.bookableServices) && parsed.onboardingData.bookableServices.every((s: any) => typeof s === 'object' && s !== null && 'name' in s && 'duration' in s && typeof s.name === 'string' && typeof s.duration === 'number')
            ? parsed.onboardingData.bookableServices
            : initialOnboardingData.bookableServices,
          defaultAppointmentDurationMinutes: parsed.onboardingData.defaultAppointmentDurationMinutes ?? initialOnboardingData.defaultAppointmentDurationMinutes,
          knowledgeBaseUrls: [], // Always initialize as empty array, no longer collected
          callActivationSettings: parsed.onboardingData.callActivationSettings ?? initialOnboardingData.callActivationSettings,
          aiCallingHours: parsed.onboardingData.aiCallingHours ?? initialOnboardingData.aiCallingHours,
        };
        
        return {
          initialStep: validStep,
          initialData: restoredOnboardingData
        };
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY); 
      }
    } catch (e) {
      console.error("Failed to parse onboarding progress from session storage:", e);
      sessionStorage.removeItem(SESSION_STORAGE_KEY); 
    }
  }
  // Ensure knowledgeBaseUrls is empty in the truly initial state as well
  return { initialStep: 1, initialData: { ...initialOnboardingData, knowledgeBaseUrls: [] } };
};


const OnboardingPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { initialStep, initialData } = getInitialStateFromStorage();
  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>(initialData);

  const updateData = useCallback((update: Partial<OnboardingData>) => {
    setOnboardingData((prev) => ({ ...prev, ...update }));
  }, []);

  const goToNextStep = useCallback(() => {
    if (currentStep < TOTAL_ONBOARDING_STEPS) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  }, [currentStep]);

  const goToPrevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  }, [currentStep]);
  
  const setStep = (step: number) => { 
      if (step >= 1 && step <= TOTAL_ONBOARDING_STEPS) {
          setCurrentStep(step);
          window.scrollTo(0,0);
      }
  };

  const { data: userSettings, isLoading: settingsLoading, error: settingsError } = useQuery<UserSettings | null>({
    queryKey: ['userSettingsOnboarding', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('user_settings')
        .select('user_id, onboarding_completed, vapi_phone_number, phone_provisioning_status, twilio_phone_number') 
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) { throw error; }
      return data;
    },
    enabled: !!user && !authLoading,
    staleTime: 1 * 60 * 1000, 
    refetchOnWindowFocus: true,
  });

   useEffect(() => {
       if (userSettings && currentStep === 5) { 
            const dbStatus = userSettings.phone_provisioning_status as OnboardingData['provisioningStatus'] | null;
            const dbPhoneNumber = userSettings.twilio_phone_number || userSettings.vapi_phone_number || null;

           if (dbStatus && (dbStatus === 'success' || dbStatus === 'failed') && dbStatus !== onboardingData.provisioningStatus) {
                console.log(`Syncing DB provisioning status (${dbStatus}) to local state.`);
                updateData({
                    provisioningStatus: dbStatus,
                    assignedPhoneNumber: dbPhoneNumber,
                });
           }
       }
   }, [userSettings, currentStep, onboardingData.provisioningStatus, updateData]);

  useEffect(() => {
    if (!settingsLoading && !settingsError && userSettings?.onboarding_completed) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      navigate('/dashboard/dashboard', { replace: true });
    }
  }, [userSettings, settingsLoading, settingsError, navigate]);

  useEffect(() => {
    if (currentStep < TOTAL_ONBOARDING_STEPS && !userSettings?.onboarding_completed) {
      // Ensure knowledgeBaseUrls is always an empty array when saving to session storage
      const stateToSave = { currentStep, onboardingData: { ...onboardingData, knowledgeBaseUrls: [] } };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
    } else if (userSettings?.onboarding_completed) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, [currentStep, onboardingData, userSettings?.onboarding_completed]);

  const { mutateAsync: saveSettingsMutation, isPending: isSavingSettings } = useMutation({
    mutationFn: async (dataToSave: OnboardingData) => {
      if (!user?.id) throw new Error("User not authenticated");
      console.log("Attempting to save onboarding settings and services:", dataToSave);
      
      const settingsToUpdate = {
        business_name: dataToSave.businessName,
        ai_greeting_message: dataToSave.aiGreetingMessage,
        reply_to_email: (dataToSave as any).replyToEmail,
        ai_forwarding_number: dataToSave.forwardingNumber || null,
        chosen_area_code: dataToSave.areaCode,
        ai_voice_id: dataToSave.aiVoiceId,
        ai_voice_provider: dataToSave.aiVoiceProvider,
        ai_name: dataToSave.aiName,
        hipaa_enabled: dataToSave.hipaa_enabled,
        ai_recording_preference: dataToSave.ai_recording_preference,
        business_hours_raw: dataToSave.businessHoursRaw || null,
        business_address: dataToSave.businessAddress || null,
        // knowledge_base_urls REMOVED from here
        call_activation_settings: dataToSave.callActivationSettings,
        ai_calling_hours: dataToSave.aiCallingHours,
        default_appointment_duration_minutes: dataToSave.defaultAppointmentDurationMinutes,
        phone_provisioning_status: dataToSave.isAreaCodeAvailable === true && dataToSave.areaCodeCheckStatus === 'checked'
            ? 'availability_checked' 
            : (dataToSave.provisioningStatus ? dataToSave.provisioningStatus : undefined), 
      };
      const cleanedSettingsToUpdate = Object.fromEntries(Object.entries(settingsToUpdate).filter(([, value]) => value !== undefined));

      console.log("Calling Supabase user_settings update with:", cleanedSettingsToUpdate);
      const { error: settingsSaveError } = await supabase
        .from('user_settings')
        .update(cleanedSettingsToUpdate)
        .eq('user_id', user.id);

      if (settingsSaveError) {
        console.error("Error saving user settings:", settingsSaveError);
        throw new Error(`Failed to save general settings: ${settingsSaveError.message}`);
      }
      console.log("General user settings save successful.");

      console.log("Attempting to update bookable services...");
      const { error: deleteError } = await supabase
        .from('business_services')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) {
        console.error("Error clearing existing services:", deleteError);
        throw new Error(`Failed to clear old services: ${deleteError.message}. Services not updated.`);
      }
      console.log("Old services cleared (if any).");

      if (dataToSave.bookableServices.length > 0) {
        const servicesToInsert = dataToSave.bookableServices.map((service: BookableService) => ({
            user_id: user.id,
            service_name: service.name,
            duration_minutes: service.duration,
            service_description: service.description ?? null,
        }));

        console.log("Inserting new services:", servicesToInsert);
        const { error: insertServicesError } = await supabase
            .from('business_services')
            .insert(servicesToInsert);

        if (insertServicesError) {
            console.error("Error saving new bookable services:", insertServicesError);
            throw new Error(`Failed to save new services: ${insertServicesError.message}.`);
        }
        console.log("Bookable services saved successfully.");
      } else {
        console.log("No bookable services to save (all existing were cleared).");
      }
       return true; 
    },
    onError: (error: Error) => {
      console.error("Mutation error during save process:", error);
      toast.error(error.message || "An unexpected error occurred while saving.");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSettingsOnboarding', user?.id]}); 
    }
  });

  const triggerSave = useCallback(async (): Promise<boolean> => {
      try {
          // Ensure knowledgeBaseUrls is empty before saving
          await saveSettingsMutation({ ...onboardingData, knowledgeBaseUrls: [] });
          toast.success("Settings and Services saved successfully!"); 
          return true;
      } catch (error) {
           console.error("triggerSave encountered an error:", error); 
          return false;
      }
  }, [saveSettingsMutation, onboardingData]);

  const navigateToDashboard = useCallback(async () => {
    if (!user?.id) { 
        toast.error("Cannot finalize: User not found."); 
        return; 
    }
    const settingsQueryKey = ['userSettingsOnboarding', user.id];
    await queryClient.cancelQueries({ queryKey: settingsQueryKey });
    const previousSettings = queryClient.getQueryData<UserSettings | null>(settingsQueryKey);
    queryClient.setQueryData<UserSettings | null>(settingsQueryKey, (oldData) => ({ 
        ...(oldData ?? { user_id: user.id } as UserSettings), 
        onboarding_completed: true 
    }));
    
    sessionStorage.removeItem(SESSION_STORAGE_KEY); 
    navigate('/dashboard/dashboard'); 

    try {
      const { error } = await supabase
        .from('user_settings')
        .update({ onboarding_completed: true }) // knowledge_base_urls is not updated here, as it's part of the general settings saved earlier
        .eq('user_id', user.id);
      
      if (error) { 
        console.error("Background Error finalizing onboarding:", error); 
        toast.error("Error finalizing onboarding status in the background."); 
        queryClient.setQueryData(settingsQueryKey, previousSettings); 
      } else { 
        queryClient.invalidateQueries({ queryKey: ['userSettings', user.id] }); 
      }
    } catch (updateError: any) { 
      console.error("Unexpected error during final background update:", updateError); 
      toast.error("Unexpected error finalizing onboarding in the background."); 
      queryClient.setQueryData(settingsQueryKey, previousSettings); 
    }
  }, [user, navigate, queryClient]);

  if (authLoading || (settingsLoading && !userSettings) ) { 
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
        </div>
    );
  }

  if (!settingsLoading && userSettings?.onboarding_completed) { 
    return null; 
  }
  
  if (settingsError) {
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 text-red-600">
            <p>Error loading user settings.</p>
            <p>{settingsError.message}</p>
            <button onClick={() => queryClient.refetchQueries({ queryKey: ['userSettingsOnboarding', user?.id] })} className="mt-4 p-2 bg-blue-500 text-white rounded">
                Try Again
            </button>
        </div>
    );
  }

  const renderStep = () => {
    const commonStepProps: OnboardingStepProps = { 
      data: onboardingData, 
      updateData, 
      goToNextStep, 
      goToPrevStep, 
      setStep,
      triggerSave: triggerSave, 
      isSaving: isSavingSettings, 
    };

    switch (currentStep) {
      case 1: return <Step1Welcome goToNextStep={goToNextStep} />; 
      case 2: return <Step2BusinessBasics {...commonStepProps} />;
      case 3: return <Step3AIIdentity {...commonStepProps} />;
      case 4: return <Step4Operations {...commonStepProps} />;
      case 5: return <Step5ConnectActivate {...commonStepProps} />;
      case 6: return <Step6Complete data={onboardingData} navigateToDashboard={navigateToDashboard} />;
      default:
        console.warn(`Invalid onboarding step: ${currentStep}. Resetting to step 1.`);
        setCurrentStep(1); 
        return <Step1Welcome goToNextStep={goToNextStep} />;
    }
  };

  return (
    <OnboardingLayout currentStep={currentStep} totalSteps={TOTAL_ONBOARDING_STEPS}>
      {renderStep()}
    </OnboardingLayout>
  );
};

export default OnboardingPage;